import axios from 'axios';

import { callable } from '../firebase-session';
import { firebaseFunctionUrl } from '../firebase-functions';
import {
  CreateFormRequest,
  FormFolderDoc,
  FormTemplateFolderDoc,
  GetFormsRequest,
  GetFormsToAnswerRequest,
  UpdateFormRequest,
  GetFormTemplatesRequest,
  CreateFormTemplateRequest,
  UpdateFormTemplateRequest,
  CreateFormFromTemplateRequest,
  FormTemplateDoc,
} from '../types';
import { ChatUser, FormAnswer, FormDoc, FormParticipantInvite, FormRespondent } from '../../types';

export const createFormRequest = async ({
  companyId,
  userId,
  userName,
  title,
  description = '',
  questions = [],
  startDate = null,
  endDate = null,
  allowMultipleResponses = false,
  status = 'draft',
  signature = false,
  clockInBlock = false,
  templateId = null,
  folderId = null,
}: CreateFormRequest) => {
  const fn = callable('createForm');
  const result = await fn({
    companyId,
    userId,
    userName,
    title,
    description,
    questions,
    startDate,
    endDate,
    allowMultipleResponses,
    status,
    signature,
    clockInBlock,
    templateId,
    folderId,
  });
  return result.data as { formId: string };
};

export const getFormsRequest = async ({
  offset,
  limit,
  companyId,
  userId,
  status,
  search,
  substring,
  sortBy,
  sortOrder,
  filterType,
  folderId,
  includeDescendants,
}: GetFormsRequest) => {
  const fn = callable('getForms');
  const result = await fn({
    companyId,
    userId,
    status,
    limit,
    offset,
    search: search ?? substring,
    sortBy,
    sortOrder,
    filterType,
    folderId,
    includeDescendants,
  });
  return result.data as { forms: FormDoc[]; total: number };
};

export const getFormFoldersRequest = async ({
  companyId,
  userId,
  search = null,
  parentId,
  limit,
  archived,
}: {
  companyId: string;
  userId: string;
  search?: string | null;
  parentId?: string | null;
  limit?: number;
  archived?: boolean;
}) => {
  const fn = callable('getFormFolders');
  const result = await fn({ companyId, userId, search, parentId, limit, archived });
  const data = result.data as { folders: FormFolderDoc[] };
  const folders = [...(data.folders ?? [])].sort((a, b) =>
    (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'base' })
  );
  return { folders };
};

export const createFormFolderRequest = async ({
  companyId,
  userId,
  name,
  parentId = null,
  colour,
}: {
  companyId: string;
  userId: string;
  name: string;
  parentId?: string | null;
  colour?: string | null;
}) => {
  const fn = callable('createFormFolder');
  const payload: Record<string, unknown> = { companyId, userId, name, parentId };
  if (colour !== undefined) payload.colour = colour;
  const result = await fn(payload);
  return result.data as { success: boolean; folderId: string };
};

export const updateFormFolderRequest = async ({
  companyId,
  folderId,
  userId,
  name,
  colour,
}: {
  companyId: string;
  folderId: string;
  userId: string;
  name?: string;
  colour?: string | null;
}) => {
  const fn = callable('updateFormFolder');
  const payload: Record<string, unknown> = { companyId, folderId, userId };
  if (name !== undefined) payload.name = name;
  if (colour !== undefined) payload.colour = colour;
  const result = await fn(payload);
  return result.data as { success: boolean };
};

export const archiveFormFolderRequest = async ({
  companyId,
  folderId,
  userId,
  archived,
}: {
  companyId: string;
  folderId: string;
  userId: string;
  archived: boolean;
}) => {
  const fn = callable('archiveFormFolder');
  const result = await fn({ companyId, folderId, userId, archived });
  return result.data as { success: boolean };
};

export const deleteFormFolderRequest = async ({
  companyId,
  folderId,
  userId,
  mode = 'blockIfChildrenOrForms',
}: {
  companyId: string;
  folderId: string;
  userId: string;
  mode?: 'blockIfChildrenOrForms' | 'cascade';
}) => {
  const fn = callable('deleteFormFolder');
  const result = await fn({ companyId, folderId, userId, mode });
  return result.data as { success: boolean };
};

export const moveFormFolderRequest = async ({
  companyId,
  folderId,
  newParentId,
  userId,
}: {
  companyId: string;
  folderId: string;
  newParentId: string | null;
  userId: string;
}) => {
  const fn = callable('moveFormFolder');
  const result = await fn({ companyId, folderId, newParentId, userId });
  return result.data as { success: boolean };
};

export const backfillFormFoldersHierarchyRequest = async ({
  companyId,
  userId,
}: {
  companyId: string;
  userId: string;
}) => {
  const fn = callable('backfillFormFoldersHierarchy');
  const result = await fn({ companyId, userId });
  return result.data as { success: boolean };
};

export const addFormToFolderRequest = async ({
  companyId,
  formId,
  folderId,
  userId,
}: {
  companyId: string;
  formId: string;
  folderId: string;
  userId: string;
}) => {
  const fn = callable('addFormToFolder');
  const result = await fn({ companyId, formId, folderId, userId });
  return result.data as { success: boolean };
};

export const removeFormFromFolderRequest = async ({
  companyId,
  formId,
  userId,
}: {
  companyId: string;
  formId: string;
  userId: string;
}) => {
  const fn = callable('removeFormFromFolder');
  const result = await fn({ companyId, formId, userId });
  return result.data as { success: boolean };
};

export const getFormsToAnswerRequest = async ({
  companyId,
  participant,
  limit = 20,
  offset = 0,
}: GetFormsToAnswerRequest) => {
  const fn = callable('getFormsForParticipant');
  const result = await fn({
    companyId,
    participant,
    limit,
    offset,
  });
  return result.data as { forms: FormDoc[]; total: number };
};

export const getFormRequest = async ({
  companyId,
  formId,
  userId,
}: {
  companyId: string;
  formId: string;
  userId: string;
}) => {
  const fn = callable('getForm');
  const result = await fn({ companyId, formId, userId });
  return result.data as FormDoc;
};

export const updateFormRequest = async ({
  companyId,
  formId,
  userId,
  title,
  description,
  questions,
  startDate,
  endDate,
  allowMultipleResponses,
  status,
  signature,
  clockInBlock,
  folderId,
}: UpdateFormRequest) => {
  const fn = callable('updateForm');
  const result = await fn({
    companyId,
    formId,
    userId,
    title,
    description,
    questions,
    startDate,
    endDate,
    allowMultipleResponses,
    status,
    signature,
    clockInBlock,
    folderId,
  });
  return result.data as FormDoc;
};

export const deleteFormRequest = async ({
  companyId,
  formId,
  userId,
}: {
  companyId: string;
  formId: string;
  userId: string;
}) => {
  const fn = callable('deleteForm');
  const result = await fn({ companyId, formId, userId });
  return result.data as { success: boolean };
};

export const sendFormRequest = async ({
  companyId,
  formId,
  user,
  participants,
}: {
  companyId: string;
  formId: string;
  user: ChatUser;
  participants: FormParticipantInvite[];
}) => {
  const fn = callable('sendFormToUsers');
  const result = await fn({ companyId, formId, user, participants });
  return result.data as { success: boolean };
};

export const submitFormResponseRequest = async ({
  companyId,
  formId,
  participant,
  answers = [],
  startedAt = null,
}: {
  companyId: string;
  formId: string;
  participant: ChatUser;
  answers: FormAnswer[];
  startedAt?: string | null;
}) => {
  const fn = callable('submitFormResponse');
  const result = await fn({ companyId, formId, participant, answers, startedAt });
  return result.data as { success: boolean };
};

export const getParticipantFormRequest = async ({
  companyId,
  formId,
  participant,
}: {
  companyId: string;
  formId: string;
  participant: ChatUser;
}) => {
  const fn = callable('getFormForParticipant');
  const result = await fn({ companyId, formId, participant });
  return result.data as FormDoc;
};

export const duplicateFormRequest = async ({
  companyId,
  formId,
  userId,
}: {
  companyId: string;
  formId: string;
  userId: string;
}) => {
  const fn = callable('duplicateForm');
  const result = await fn({ companyId, formId, userId });
  return result.data as { formId: string };
};

export const updateFormStatusRequest = async ({
  companyId,
  formId,
  userId,
  status,
}: {
  companyId: string;
  formId: string;
  userId: string;
  status: 'draft' | 'published' | 'archived';
}) => {
  const fn = callable('updateFormStatus');
  const result = await fn({ companyId, formId, userId, status });
  return result.data as { success: boolean };
};

export const getFormSummaryRequest = async ({
  companyId,
  formId,
  userId,
}: {
  companyId: string;
  formId: string;
  userId: string;
}) => {
  const fn = callable('getFormSummary');
  const result = await fn({ companyId, formId, userId });
  return result.data as any;
};

export const getFormParticipantsRequest = async ({
  companyId,
  formId,
  userId,
  search,
  status,
  limit,
  offset,
  sortField,
  sortDirection,
}: {
  companyId: string;
  formId: string;
  userId: string;
  search?: string | null;
  status?: string | null;
  limit?: number;
  offset?: number;
  sortField?: 'name' | 'email' | 'phone' | 'status' | 'invitedAt' | null;
  sortDirection?: 'asc' | 'desc' | null;
}) => {
  const fn = callable('getFormParticipants');
  const result = await fn({
    companyId,
    formId,
    userId,
    search: search || null,
    status: status || null,
    ...(limit != null ? { limit } : {}),
    ...(offset != null ? { offset } : {}),
    ...(sortField != null ? { sortField } : {}),
    ...(sortDirection != null ? { sortDirection } : {}),
  });
  return result.data as any;
};

export const getUserFormAnswersByAdminRequest = async ({
  companyId,
  formId,
  adminId,
  targetUserId,
}: {
  companyId: string;
  formId: string;
  adminId: string;
  targetUserId: string;
}) => {
  const fn = callable('getUserFormAnswersByAdmin');
  const result = await fn({ companyId, formId, adminId, targetUserId });
  return result.data as any;
};

export const sendFormMessageRequest = async ({
  companyId,
  formId,
  userId,
  participantIds = null,
  sendToAll = false,
}: {
  companyId: string;
  formId: string;
  userId: string;
  participantIds?: string[] | null;
  sendToAll?: boolean;
}) => {
  const fn = callable('sendFormMessage');
  const result = await fn({ companyId, formId, userId, participantIds, sendToAll });
  return result.data as any;
};

export const removeFormParticipantRequest = async ({
  companyId,
  formId,
  userId,
  participantId,
}: {
  companyId: string;
  formId: string;
  userId: string;
  participantId: string;
}) => {
  const fn = callable('removeFormParticipant');
  const result = await fn({ companyId, formId, userId, participantId });
  return result.data as any;
};

export const removeFormResourceIdsRequest = async ({
  companyId,
  formId,
  userId,
  resourceIds,
}: {
  companyId: string;
  formId: string;
  userId: string;
  resourceIds: string[];
}) => {
  const fn = callable('removeFormResourceIds');
  const result = await fn({ companyId, formId, userId, resourceIds });
  return result.data as any;
};

export const addFormResourceIdsRequest = async ({
  companyId,
  formId,
  userId,
  resourceIds,
}: {
  companyId: string;
  formId: string;
  userId: string;
  resourceIds: string[];
}) => {
  const fn = callable('addFormResourceIds');
  const result = await fn({ companyId, formId, userId, resourceIds });
  return result.data as any;
};

export const addUsersToFormsByResourceRequest = async ({
  companyId,
  resourceId,
  users,
  token,
}: {
  companyId: string;
  resourceId: string;
  users: any[];
  token: string;
}) => {
  return axios.post(
    firebaseFunctionUrl('syncformparticipantsbyresourcewebhook'),
    { companyId, resourceId, users, timepstamp: new Date().toISOString() },
    { headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` } }
  );
};

// Form template API
export const getFormTemplatesRequest = async ({
  companyId,
  userId,
  limit = 20,
  offset = 0,
  search = null,
  folderId,
  archived,
}: GetFormTemplatesRequest) => {
  const fn = callable('getFormTemplates');
  const result = await fn({ companyId, userId, limit, offset, search, folderId, archived });
  return result.data as { templates: FormTemplateDoc[]; total: number; offset: number; hasMore: boolean };
};

export const getFormTemplateRequest = async ({
  companyId,
  templateId,
  userId,
}: {
  companyId: string;
  templateId: string;
  userId: string;
}) => {
  const fn = callable('getFormTemplate');
  const result = await fn({ companyId, templateId, userId });
  return result.data as FormTemplateDoc;
};

export const createFormTemplateRequest = async ({
  companyId,
  userId,
  userName = null,
  title,
  description = '',
  questions = [],
  signature = false,
  clockInBlock = false,
  folderId = null,
}: CreateFormTemplateRequest) => {
  const fn = callable('createFormTemplate');
  const result = await fn({
    companyId,
    userId,
    userName,
    title,
    description,
    questions,
    signature,
    clockInBlock,
    folderId,
  });
  return result.data as { success: boolean; templateId: string };
};

export const updateFormTemplateRequest = async ({
  companyId,
  templateId,
  userId,
  title,
  description,
  questions,
  signature,
  clockInBlock,
  folderId,
}: UpdateFormTemplateRequest) => {
  const fn = callable('updateFormTemplate');
  const result = await fn({
    companyId,
    templateId,
    userId,
    title,
    description,
    questions,
    signature,
    clockInBlock,
    folderId,
  });
  return result.data as { success: boolean };
};

export const deleteFormTemplateRequest = async ({
  companyId,
  templateId,
  userId,
}: {
  companyId: string;
  templateId: string;
  userId: string;
}) => {
  const fn = callable('deleteFormTemplate');
  const result = await fn({ companyId, templateId, userId });
  return result.data as { success: boolean };
};

export const createFormFromTemplateRequest = async ({
  companyId,
  templateId,
  userId,
  userName = null,
  customizations = {},
  saveAs = 'form',
}: CreateFormFromTemplateRequest) => {
  const fn = callable('createFormFromTemplate');
  const result = await fn({
    companyId,
    templateId,
    userId,
    userName,
    customizations,
    saveAs,
  });
  return result.data as { success: boolean; formId?: string; templateId: string };
};

export const createFormFromTemplateForFillInRequest = async ({
  companyId,
  templateId,
  userId,
  userName = null,
  userEmail = null,
  userPhone = null,
}: {
  companyId: string;
  templateId: string;
  userId: string;
  userName?: string | null;
  userEmail?: string | null;
  userPhone?: string | null;
}) => {
  const fn = callable('createFormFromTemplateForFillIn');
  const result = await fn({
    companyId,
    templateId,
    userId,
    userName,
    userEmail,
    userPhone,
  });
  return result.data as { success: boolean; formId: string; templateId: string };
};

export const duplicateFormTemplateRequest = async ({
  companyId,
  templateId,
  userId,
  newTitle,
  userName = null,
}: {
  companyId: string;
  templateId: string;
  userId: string;
  newTitle?: string;
  userName?: string | null;
}) => {
  const fn = callable('duplicateFormTemplate');
  const result = await fn({ companyId, templateId, userId, newTitle, userName });
  return result.data as { success: boolean; templateId: string };
};

export const archiveFormTemplateRequest = async ({
  companyId,
  templateId,
  userId,
  archived,
}: {
  companyId: string;
  templateId: string;
  userId: string;
  archived: boolean;
}) => {
  const fn = callable('archiveFormTemplate');
  const result = await fn({ companyId, templateId, userId, archived });
  return result.data as { success: boolean };
};

export const getFormTemplateFoldersRequest = async ({
  companyId,
  userId,
  search = null,
  parentId,
  limit,
  archived,
}: {
  companyId: string;
  userId: string;
  search?: string | null;
  parentId?: string | null;
  limit?: number;
  archived?: boolean;
}) => {
  const fn = callable('getFormTemplateFolders');
  const result = await fn({ companyId, userId, search, parentId, limit, archived });
  const data = result.data as { folders: FormTemplateFolderDoc[] };
  const folders = [...(data.folders ?? [])].sort((a, b) =>
    (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'base' })
  );
  return { folders };
};

export const createFormTemplateFolderRequest = async ({
  companyId,
  userId,
  name,
  parentId = null,
  colour,
}: {
  companyId: string;
  userId: string;
  name: string;
  parentId?: string | null;
  colour?: string | null;
}) => {
  const fn = callable('createFormTemplateFolder');
  const payload: Record<string, unknown> = { companyId, userId, name, parentId };
  if (colour !== undefined) payload.colour = colour;
  const result = await fn(payload);
  return result.data as { success: boolean; folderId: string };
};

export const updateFormTemplateFolderRequest = async ({
  companyId,
  folderId,
  userId,
  name,
  colour,
}: {
  companyId: string;
  folderId: string;
  userId: string;
  name?: string;
  colour?: string | null;
}) => {
  const fn = callable('updateFormTemplateFolder');
  const payload: Record<string, unknown> = { companyId, folderId, userId };
  if (name !== undefined) payload.name = name;
  if (colour !== undefined) payload.colour = colour;
  const result = await fn(payload);
  return result.data as { success: boolean };
};

export const archiveFormTemplateFolderRequest = async ({
  companyId,
  folderId,
  userId,
  archived,
}: {
  companyId: string;
  folderId: string;
  userId: string;
  archived: boolean;
}) => {
  const fn = callable('archiveFormTemplateFolder');
  const result = await fn({ companyId, folderId, userId, archived });
  return result.data as { success: boolean };
};

export const moveFormTemplateFolderRequest = async ({
  companyId,
  folderId,
  newParentId,
  userId,
}: {
  companyId: string;
  folderId: string;
  newParentId: string | null;
  userId: string;
}) => {
  const fn = callable('moveFormTemplateFolder');
  const result = await fn({ companyId, folderId, newParentId, userId });
  return result.data as { success: boolean };
};

export const deleteFormTemplateFolderRequest = async ({
  companyId,
  folderId,
  userId,
  mode = 'blockIfChildrenOrForms',
}: {
  companyId: string;
  folderId: string;
  userId: string;
  mode?: 'blockIfChildrenOrForms' | 'cascade';
}) => {
  const fn = callable('deleteFormTemplateFolder');
  const result = await fn({ companyId, folderId, userId, mode });
  return result.data as { success: boolean };
};

export const addFormTemplateToFolderRequest = async ({
  companyId,
  templateId,
  folderId,
  userId,
}: {
  companyId: string;
  templateId: string;
  folderId: string;
  userId: string;
}) => {
  const fn = callable('addFormTemplateToFolder');
  const result = await fn({ companyId, templateId, folderId, userId });
  return result.data as { success: boolean };
};

export const removeFormTemplateFromFolderRequest = async ({
  companyId,
  templateId,
  userId,
}: {
  companyId: string;
  templateId: string;
  userId: string;
}) => {
  const fn = callable('removeFormTemplateFromFolder');
  const result = await fn({ companyId, templateId, userId });
  return result.data as { success: boolean };
};

export const getRespondentsByAnswerRequest = async ({
  companyId,
  formId,
  userId,
  questionId,
  answerValue,
  search = null,
  limit = 25,
  offset = 0,
  sortField = 'name',
  sortDirection = 'asc',
}: {
  companyId: string;
  formId: string;
  userId: string;
  questionId: string;
  answerValue: string;
  search?: string | null;
  limit?: number;
  offset?: number;
  sortField?: string;
  sortDirection?: 'asc' | 'desc';
}) => {
  const fn = callable('getRespondentsByAnswer');
  const result = await fn({
    companyId,
    formId,
    userId,
    questionId,
    answerValue,
    search,
    limit,
    offset,
    sortField,
    sortDirection,
  });
  return result.data as { respondents: FormRespondent[]; total: number };
};
